import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getTranslateByKey } from '@/main/services/utils';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import * as S from './styles';
import { KOGAreaButtons } from '../../styles';
import KogInputAmountControlled from '../../inputText/inputAmountControlled';
import { SupplierCompanyRelationResponse } from '@/main/models/response/supplierCompanyRelationResponse';
import { supplierApi } from '@/main/api';
import { currencyToNumber, formatCurrency } from '@/main/services/utilsNumber';
import { toast } from 'react-toastify';
import { isEmpty, isNil } from 'lodash';
import { FormControlLabel, Switch } from '@mui/material';
import useAsyncModalConfirmReason from '@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason';
import { toBase64 } from '@/main/services/utilsFile';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';

interface Props {
  open: boolean;
  closeModal: () => void;
  resetGrid: () => void;
  supplier: SupplierCompanyRelationResponse;
}

const INITIAL_FORM_STATE_SAVE = {
  refundLimit: null as number | string | null
};

type IFormModel = typeof INITIAL_FORM_STATE_SAVE;

function MySuppliersEdit({ open, closeModal, resetGrid, supplier }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTrusted, setIsTrusted] = useState<boolean>(false);
  const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

  const methodSave = useForm<IFormModel>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE_SAVE
  });

  useEffect(() => {
    if (!isNil(supplier.refundLimit)) {
      methodSave.setValue(
        'refundLimit',
        formatCurrency(supplier.refundLimit, true)
      );
    }
    if (!isNil(supplier.isTrusted)) {
      setIsTrusted(supplier.isTrusted);
    }
  }, [supplier]);

  async function onSubmit(dataForm: IFormModel) {
    const refundLimit =
      !isNil(dataForm.refundLimit) && !isEmpty(dataForm.refundLimit)
        ? Number(currencyToNumber(dataForm.refundLimit as string))
        : undefined;

    const resultConfirm = await getConfirmationReason(
      getTranslateByKey('information.confirmChangeTitle'),
      ReasonTypeEnum.ChangeCompany
    );

    if (!resultConfirm.confirmed) return;

    let filesRequest = undefined;
    if (resultConfirm.files && resultConfirm.files.length > 0) {
      filesRequest = await Promise.all(
        resultConfirm.files.map(async item => {
          const base64DataUrl = await toBase64(item);
          return {
            fileName: item.name,
            contentType: item.type,
            fileBase64: base64DataUrl as string
          };
        })
      );
    }

    try {
      setIsLoading(true);

      await supplierApi.changeSupplierRelationInfo(supplier.guid, {
        refundLimit,
        isTrusted,
        changeJustification: {
          guidReason: String(resultConfirm.reason?.key),
          reasonDescription: resultConfirm.observation,
          filesUpload: filesRequest
        }
      });

      toast.success(getTranslateByKey('information.supplierEditSuccessfully'));
      closeModal();
      resetGrid();
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <KogModalSideComponent
      open={open}
      closeModal={() => closeModal()}
      id="id-mysuppliers-modal-edit"
      titleModalReducer={getTranslateByKey('information.refundLimit')}
      title={<S.Title>{getTranslateByKey('information.refundLimit')}</S.Title>}>
      <ModalConfirmationReason />
      <form onSubmit={methodSave.handleSubmit(onSubmit)} noValidate>
        <S.TextContainer className="d-flex gap-1" style={{ marginBottom: 10 }}>
          <b>{getTranslateByKey('information.rfc')}:</b>
          <span>{supplier.registrationNumber}</span>
        </S.TextContainer>

        <S.TextContainer className="d-flex gap-1" style={{ marginBottom: 10 }}>
          <b>{getTranslateByKey('information.supplierCompany')}:</b>
          <span>{supplier.companyName}</span>
        </S.TextContainer>

        <S.TextContainer className="d-flex gap-1" style={{ marginBottom: 20 }}>
          <b>{getTranslateByKey('information.userDateTime')}:</b>
          <span>{supplier.userDateTime}</span>
        </S.TextContainer>

<S.WrapperInputAmount>
        <KogInputAmountControlled
          acceptZero
          typeInput="outlined"
          id="id-mySuppliersRegister-refundLimit"
          name="refundLimit"
          label={getTranslateByKey('information.refundLimit')}
          control={methodSave.control}
          
          controllerProps={{
            rules: { maxLength: 18 }
          }}
          customInputProps={{
            autoFocus: true
          }}
        />
</S.WrapperInputAmount>

        <FormControlLabel
          style={{ marginTop: 15 }}
          control={
            <Switch
              checked={isTrusted}
              onChange={(_, checked) => setIsTrusted(checked)}
              color="primary"
            />
          }
          label={getTranslateByKey('information.trustedSupplier')}
          labelPlacement="start"
        />

        <KOGAreaButtons
          style={{ marginTop: 30, display: 'flex', justifyContent: 'center' }}>
          <SecondaryButton
            id="id-mySuppliersRegister-button-save"
            type="submit"
            disabled={isLoading}>
            {getTranslateByKey('information.save')}
          </SecondaryButton>
        </KOGAreaButtons>
      </form>
    </KogModalSideComponent>
  );
}

export default MySuppliersEdit;
